import { useMemo } from "react";
import { useCategories } from "../../../../shared/stores/category-store";
import {
  getDayName,
  getDayNumber,
  getToday,
} from "../../../../shared/types/date-helpers";

interface WeeklyStatsTableProps {
  readonly weekDays: string[];
  readonly dayByCat: Map<string, Record<string, number>>;
  readonly categoryTotals: Record<string, number>;
}

function formatHours(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (m === 0) return `${h}h`;
  return `${h}h${String(m).padStart(2, "0")}`;
}

export function WeeklyStatsTable({
  weekDays,
  dayByCat,
  categoryTotals,
}: WeeklyStatsTableProps) {
  const { categories, labels } = useCategories();

  // Calculer le total de chaque jour pour trouver le plus productif
  const dayTotals = useMemo(() => {
    return weekDays.map((day) => {
      const row = dayByCat.get(day) ?? {};
      return {
        day,
        total: categories.reduce((acc, cat) => acc + (row[cat] ?? 0), 0),
      };
    });
  }, [weekDays, dayByCat, categories]);

  const maxDayTotal = Math.max(...dayTotals.map((d) => d.total), 1);
  const today = getToday();

  return (
    <div className="bg-surface border border-border rounded-xl overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-surface-alt/50">
            <th className="px-3 py-2.5 text-left text-xs font-semibold text-ink-muted">
              Jour
            </th>
            {categories.map((cat) => (
              <th
                key={cat}
                className="px-2 py-2.5 text-center text-xs font-semibold text-ink"
              >
                {labels[cat].slice(0, 4)}.
              </th>
            ))}
            <th className="px-2 py-2.5 text-center text-xs font-semibold text-ink-muted">
              Total
            </th>
          </tr>
        </thead>
        <tbody>
          {dayTotals.map(({ day, total: rowTotal }) => {
            const row = dayByCat.get(day) ?? {};
            const isMostProductive = rowTotal === maxDayTotal && rowTotal > 0;
            const isToday = day === today;

            return (
              <tr
                key={day}
                className={`border-b border-border last:border-b-0 transition-colors ${
                  isToday ? "bg-accent/10" : ""
                }`}
                style={isMostProductive && !isToday ? { backgroundColor: "rgba(22, 163, 74, 0.08)" } : undefined}
              >
                <td className={`px-3 py-2 text-xs whitespace-nowrap ${
                  isToday ? "font-semibold text-accent" : "text-ink-muted"
                }`}>
                  <span className="flex items-center gap-1.5">
                    {getDayName(day)} {getDayNumber(day)}
                    {isToday && (
                      <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                    )}
                  </span>
                </td>
                {categories.map((cat) => (
                  <td
                    key={cat}
                    className="px-2 py-2 text-center text-xs"
                  >
                    <span
                      className={`inline-block min-w-[2.5rem] ${
                        (row[cat] ?? 0) > 0 ? "font-medium text-ink" : "text-ink-faint"
                      }`}
                    >
                      {(row[cat] ?? 0) > 0 ? formatHours(row[cat] ?? 0) : "—"}
                    </span>
                  </td>
                ))}
                <td className="px-2 py-2 text-center text-xs">
                  <span className="inline-block font-semibold text-ink">
                    {rowTotal > 0 ? formatHours(rowTotal) : "—"}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
        {/* Ligne total hebdomadaire */}
        <tfoot>
          <tr className="border-t-2 border-border bg-surface-alt/30">
            <td className="px-3 py-2.5 text-xs font-bold text-ink">
              Total
            </td>
            {categories.map((cat) => (
              <td
                key={cat}
                className="px-2 py-2.5 text-center text-xs font-semibold"
              >
                <span className={categoryTotals[cat] > 0 ? "text-ink" : "text-ink-faint"}>
                  {categoryTotals[cat] > 0 ? formatHours(categoryTotals[cat]) : "—"}
                </span>
              </td>
            ))}
            <td className="px-2 py-2.5 text-center text-xs font-bold text-ink">
              {formatHours(
                categories.reduce((acc, cat) => acc + (categoryTotals[cat] ?? 0), 0)
              )}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
